// Import required utils
import { getDocumentElement, getDocumentElements, updateFieldTotal } from '../../Utils/Utils';

/**
 * Retrieve the zone rainfall based on the regional rainfall and garden
 * type.
 * 
 * @param   {integer}   rainfall
 * @param   {string}    gardenType
 * 
 * @returns {null|integer}
 */
const GetZoneRainfall = (rainfall, gardenType) => {
    const ZoneRainfallValues = [
        {
            name: 'Native garden',
            lessThan500: 150,
            between500and800: 100,
            greaterThan800: 50
        },
        {
            name: 'Lawn with shrubs',
            lessThan500: 450,
            between500and800: 300,
            greaterThan800: 150
        },
        {
            name: 'Vegetable garden',
            lessThan500: 900,
            between500and800: 600,
            greaterThan800: 300
        },
        {
            name: 'Other'
        }
    ];
    const Zone = ZoneRainfallValues.find(item => item.name === gardenType);
    let zoneRainfall = 0;

    if (rainfall < 500 && rainfall > 0) {
        zoneRainfall = Zone.lessThan500;
    } else if (rainfall >= 500 && rainfall < 800) {
        zoneRainfall = Zone.between500and800;
    } else if (rainfall > Zone.greaterThan800) {
        zoneRainfall = Zone.greaterThan800;
    }

    return zoneRainfall;
};

/**
 * Run the calculation for the garden water use handler. Zone rainfall is
 * determined by the garden type and the rainfall per mm for the specified
 * zone the user selected in the property details accordion. This value must
 * be populated in order for the calculation to run.
 * 
 * Calculation shown below  (answer will be in Megalitres)
 * Zone rainfall * gardenArea ÷ 1,000,000
 * 
 * @param   {HTMLElement}   rainfall
 * @param   {HTMLElement}   gardenType
 * @param   {HTMLElement}   gardenArea
 * 
 * @returns {void}
 */
const GardenWaterUseCalcHandler = (rainfall, gardenType, gardenArea, gardenSubtotal) => {
    if (gardenType.selectedIndex != 0 && gardenArea.value > 0) {
        const ZoneRainfall = GetZoneRainfall(
            Number(rainfall.value),
            gardenType.options[gardenType.selectedIndex].text
        );

        if (ZoneRainfall > 0) {
            gardenSubtotal.value = ((ZoneRainfall * gardenArea.value) / 1000000).toFixed(3);
        }
    } else {
        gardenSubtotal.value = ""
    }
};

/**
 * Retrieves the needed elements for the garden water calculation.
 * 
 * @param   {string}    id
 * @param   {array}     fieldData 
 * 
 * @returns {void}
 */
export const GardenWaterUseCalc = (id, fieldData) => {
    // Set the field selectors
    const RainfallSelector = fieldData[0].selectors.rainfall;
    const GardenTypeSelector = fieldData[0].selectors.gardenType;
    const GardenAreaSelector = fieldData[0].selectors.gardenArea;
    const GardenSubtotalSelector = fieldData[0].selectors.gardenSubtotal;
    const GardenTotalSelector = fieldData[0].selectors.gardenTotal;
    const GardenSummaryTotalSelector = fieldData[0].selectors.gardenSummaryTotal;

    // Retrieve all instances of the elements, we will use a loop index value to access
    // the correct fields to run the calculations.
    const RainfallField = getDocumentElement(`#${RainfallSelector}`);
    const GardenTypeField = getDocumentElements(`#${id} [id^="${GardenTypeSelector}"]`);
    const GardenAreaField = getDocumentElements(`#${id} [id^="${GardenAreaSelector}"]`);
    const GardenSubtotalField = getDocumentElements(`#${id} [id^="${GardenSubtotalSelector}"]`);
    const GardenTotalField = getDocumentElement(`#${id} [id^="${GardenTotalSelector}"]`);
    const GardenSummaryTotalField = getDocumentElement(GardenSummaryTotalSelector);
    const FieldsToWatch = [GardenTypeField, GardenAreaField];

    // We want to attach the keyup event listener to the area field
    if (GardenTotalField && GardenSummaryTotalField) {
        FieldsToWatch.forEach((fields, i) => {
            fields.forEach((field, i) => {
                field.addEventListener('input', e => {
                    setTimeout(() => {
                        e.stopImmediatePropagation();

                        GardenWaterUseCalcHandler(
                            RainfallField,
                            GardenTypeField[i],
                            GardenAreaField[i],
                            GardenSubtotalField[i],
                        );

                        updateFieldTotal(GardenSubtotalField, GardenTotalField, GardenSummaryTotalField);
                    }, 0)
                });
            })
        });
    }

    // listen for clone removed and re-update totals
    document.addEventListener('clonable:removed', e => {
        if (e.detail.id === 'garden-water-use') {
            updateFieldTotal(GardenSubtotalField, GardenTotalField, GardenSummaryTotalField);
        }
    });
};

/**
 * Retrieves the needed elements for the dairy shed calculation.
 * 
 * @param   {string}    id 
 * @param   {array}     fieldData 
 * 
 * @returns {void}
 */
export const DairyShedWaterUseCalc = (id, fieldData) => {
    // Selector for shed sub total fields
    const DairyShedSubtotalSelector = fieldData[1].selectors.dairyTotal;
    const DairyShedOverallTotalSelector = fieldData[1].selectors.dairyOverallTotal;
    const DairyShedHerdSizeSelector = fieldData[1].selectors.herdSize;
    const DairySummaryTotalSelector = fieldData[1].selectors.dairySummaryTotal;

    // Retrieve all dairy shed fields
    const DairyShedSubtotalField = getDocumentElements(`#${id} [id^="${DairyShedSubtotalSelector}"]`);
    const DairyShedHerdSizeTotalField = getDocumentElements(`#${id} [id^="${DairyShedHerdSizeSelector}"]`);
    const DairyShedOverallTotalField = getDocumentElement(DairyShedOverallTotalSelector);
    const DairySummaryTotalField = getDocumentElement(DairySummaryTotalSelector);
    const AllFields = [DairyShedSubtotalField, DairyShedHerdSizeTotalField];

    // For each sub total field, we want to bind a handler that will update the overall total.
    if (AllFields.length > 0 && DairyShedOverallTotalField && DairySummaryTotalField) {
        AllFields.forEach(fields => {
            fields.forEach(field => {
                field.addEventListener('change', () => {
                    updateFieldTotal(DairyShedSubtotalField, DairyShedOverallTotalField, DairySummaryTotalField);
                });

                field.addEventListener('input', () => {
                    updateFieldTotal(DairyShedSubtotalField, DairyShedOverallTotalField, DairySummaryTotalField);
                });
            });
        });
    }

    // listen for clone removed and re-update totals
    document.addEventListener('clonable:removed', e => {
        if (e.detail.id === 'shed-water-use') {
            updateFieldTotal(DairyShedSubtotalField, DairyShedOverallTotalField, DairySummaryTotalField);
        }
    });
}
